
import { defineComponent, ref } from "vue";
import type { Component } from "vue";
import {
  ArrowRightIcon,
  DeviceMobileIcon,
  ExternalLinkIcon,
  RefreshIcon,
  ShieldCheckIcon,
} from "@heroicons/vue/outline";
import Modal from "@/components/common/Modal.vue";
import { getLocale } from "@/i18n";
import type { Locale } from "@/i18n";

type NewsVariant = "feature" | "guide" | "security";

type NewsItem = {
  id: string;
  publishedAt: string;
  categoryKey: string;
  titleKey: string;
  summaryKey: string;
  detailsIntroKey: string;
  detailKeys: readonly string[];
  orderedDetails: boolean;
  variant: NewsVariant;
  icon: Component;
  sourceUrls: Record<Locale, string>;
};

const sectionTitleId = "dashboard-news-title";

const newsItems: readonly NewsItem[] = [
  {
    id: "egov-mobile-signing",
    publishedAt: "2026-08-14",
    categoryKey: "dashboard.news.category.feature",
    titleKey: "dashboard.news.egovMobile.title",
    summaryKey: "dashboard.news.egovMobile.summary",
    detailsIntroKey: "dashboard.news.egovMobile.details.intro",
    detailKeys: [
      "dashboard.news.egovMobile.details.step1",
      "dashboard.news.egovMobile.details.step2",
      "dashboard.news.egovMobile.details.step3",
      "dashboard.news.egovMobile.details.step4",
    ],
    orderedDetails: true,
    variant: "feature",
    icon: DeviceMobileIcon,
    sourceUrls: {
      ru: "https://pki.gov.kz/ru/egovm/",
      en: "https://pki.gov.kz/en/obtainment-of-digital-signature-keys-via-egov-mobile-app/",
    },
  },
  {
    id: "eds-expiration",
    publishedAt: "2026-08-14",
    categoryKey: "dashboard.news.category.guide",
    titleKey: "dashboard.news.expiration.title",
    summaryKey: "dashboard.news.expiration.summary",
    detailsIntroKey: "dashboard.news.expiration.details.intro",
    detailKeys: [
      "dashboard.news.expiration.details.step1",
      "dashboard.news.expiration.details.step2",
      "dashboard.news.expiration.details.step3",
      "dashboard.news.expiration.details.step4",
    ],
    orderedDetails: true,
    variant: "guide",
    icon: RefreshIcon,
    sourceUrls: {
      ru: "https://pki.gov.kz/docs/guides_ru/lk_fl/#_7",
      en: "https://pki.gov.kz/docs/guides_eng/lk_fl/#_7",
    },
  },
  {
    id: "eds-security",
    publishedAt: "2026-08-14",
    categoryKey: "dashboard.news.category.security",
    titleKey: "dashboard.news.security.title",
    summaryKey: "dashboard.news.security.summary",
    detailsIntroKey: "dashboard.news.security.details.intro",
    detailKeys: [
      "dashboard.news.security.details.rule1",
      "dashboard.news.security.details.rule2",
      "dashboard.news.security.details.rule3",
      "dashboard.news.security.details.rule4",
    ],
    orderedDetails: false,
    variant: "security",
    icon: ShieldCheckIcon,
    sourceUrls: {
      ru: "https://pki.gov.kz/ru/2025/08/28/%D1%83%D0%B2%D0%B0%D0%B6%D0%B0%D0%B5%D0%BC%D1%8B%D0%B5-%D0%BF%D0%BE%D0%BB%D1%8C%D0%B7%D0%BE%D0%B2%D0%B0%D1%82%D0%B5%D0%BB%D0%B8-5/",
      en: "https://pki.gov.kz/en/2025/08/28/dear-users-5/",
    },
  },
];

const sourceUrl = (item: NewsItem) => item.sourceUrls[getLocale()];

const formatNewsDate = (value: string) =>
  new Intl.DateTimeFormat(getLocale() === "ru" ? "ru-RU" : "en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date(`${value}T00:00:00`));

export default defineComponent({
  name: "NewsSection",
  components: {
    ArrowRightIcon,
    ExternalLinkIcon,
    Modal,
  },
  setup() {
    const selectedItem = ref<NewsItem | null>(null);

    const openDetails = (item: NewsItem) => {
      selectedItem.value = item;
    };

    const closeDetails = () => {
      selectedItem.value = null;
    };

    return {
      sectionTitleId,
      newsItems,
      selectedItem,
      sourceUrl,
      formatNewsDate,
      openDetails,
      closeDetails,
    };
  },
});
